import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
} from "react";

// ---------------------------------------------------------------------------
// Tiny class-merge helper (avoids adding clsx as a dep)
// ---------------------------------------------------------------------------
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

// ---------------------------------------------------------------------------
// PageHeader — the deep-green "hero strip" introduced by the redesign.
// Pages render it at the top, then their content sits on the cream
// canvas below.
//
// Accepts:
//   eyebrow  — small tracked-out text (uppercase) above the title
//   title    — the H1
//   subtitle — small muted text below the title
//   actions  — pinned to the right (typically a "New x" CTA)
//   tabs     — optional inline tabs anchored to the bottom edge
// ---------------------------------------------------------------------------
export function PageHeader({
  back,
  eyebrow,
  title,
  subtitle,
  actions,
  tabs,
}: {
  back?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
}) {
  return (
    <div className="-mt-6 -mx-4 sm:-mx-6 lg:-mx-8 mb-6 bg-brand-800 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 pb-0">
        {back && <div className="mb-2">{back}</div>}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            {eyebrow && (
              <div className="text-[10.5px] tracking-[0.1em] uppercase font-medium text-brand-400 mb-1.5">
                {eyebrow}
              </div>
            )}
            <h1 className="text-white">{title}</h1>
            {subtitle && (
              <div className="mt-1 text-[12.5px] text-white/45 max-w-2xl">
                {subtitle}
              </div>
            )}
            {tabs && <div className="flex gap-0.5 mt-5 -mb-px">{tabs}</div>}
          </div>
          {actions && (
            <div className="flex flex-wrap gap-2 mb-7">{actions}</div>
          )}
        </div>
        {!tabs && <div className="h-7" />}
      </div>
    </div>
  );
}

// PageHeader-internal tab. Use as <PageHeaderTab>label</PageHeaderTab>.
export function PageHeaderTab({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "px-4 py-2 text-[12.5px] rounded-t-md transition-colors",
        "border-b-2 -mb-px",
        active
          ? "text-white border-brand-400 bg-white/[0.07] font-medium"
          : "text-white/50 border-transparent hover:text-white/80",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------
export function Card({
  className,
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div className={cx("card", className)} {...props}>
      {children}
    </div>
  );
}

export function DarkCard({
  className,
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div className={cx("card-dark", className)} {...props}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
type Variant =
  | "primary"
  | "accent"
  | "secondary"
  | "ghost"
  | "on-dark"
  | "danger"
  | "success";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: "sm" | "md";
  loading?: boolean;
};

export function Button({
  variant = "secondary",
  size = "md",
  className,
  loading = false,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const klass = {
    primary: "btn-primary",
    accent: "btn-accent",
    secondary: "btn-secondary",
    ghost: "btn-ghost",
    "on-dark": "btn-on-dark",
    danger: "btn-danger",
    success: "btn-success",
  }[variant];
  return (
    <button
      className={cx(klass, size === "sm" && "btn-sm", className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <span className="spinner" />}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx("field", className)}>
      {label && <label className="label">{label}</label>}
      {children}
      {hint && <p className="mt-1.5 text-xs text-ink-400">{hint}</p>}
      {error && <p className="mt-1.5 text-xs text-status-rejected-fg">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
export function StatusBadge({ status }: { status: string }) {
  return (
    <span className="badge" data-status={status}>
      {status.replaceAll("_", " ")}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section header (within a card)
// ---------------------------------------------------------------------------
export function SectionTitle({
  children,
  hint,
}: PropsWithChildren<{ hint?: ReactNode }>) {
  return (
    <div className="mb-3">
      <h2>{children}</h2>
      {hint && <p className="mt-1 text-sm text-ink-500 font-light">{hint}</p>}
    </div>
  );
}
